/**
 * 买家端 i18n 入口（zh / en / ko）。
 * - 所有界面文案统一放在 dict.ts（中/英）与 ko.ts（韩），组件内用 t()（usePrefs）或 tr() 取值；
 * - tr() 读取当前语言（由 BuyerPrefsProvider 在渲染时同步），可在组件外的工具函数里使用；
 * - 文案支持 {name} 占位符：tr('journey.x', { count: 3 })；
 * - 韩文缺失时回退英文，再回退中文，并在开发环境打印一次告警。
 */
import { DICT, type Lang } from './dict';
import { KO } from './ko';

export type { Lang } from './dict';

export const LANGS: Lang[] = ['zh', 'en', 'ko'];

/** 语言切换器上显示的名字（各语言用自己的写法） */
export const LANG_LABELS: Record<Lang, string> = { zh: '中文', en: 'English', ko: '한국어' };

/** Intl / 语音识别使用的区域代码 */
export const LOCALES: Record<Lang, string> = { zh: 'zh-CN', en: 'en-US', ko: 'ko-KR' };

export type TrVars = Record<string, string | number | null | undefined>;

let currentLang: Lang = 'zh';

export function setCurrentLang(lang: Lang) { currentLang = lang; }
export function getCurrentLang(): Lang { return currentLang; }
export function isLang(value: unknown): value is Lang { return value === 'zh' || value === 'en' || value === 'ko'; }

/** 当前语言的 Intl 区域代码（日期格式化、国家名、语音识别） */
export function uiLocale(lang: Lang = currentLang): string { return LOCALES[lang]; }

const warned = new Set<string>();

function interpolate(text: string, vars?: TrVars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => (name in vars && vars[name] != null ? String(vars[name]) : whole));
}

/** 按指定语言取文案 */
export function translate(lang: Lang, key: string, vars?: TrVars): string {
  const entry = DICT[key];
  if (!entry) {
    if (import.meta.env?.DEV && !warned.has(key)) { warned.add(key); console.warn(`[i18n] missing key: ${key}`); }
    return key;
  }
  let text: string;
  if (lang === 'ko') {
    const ko = (KO as Record<string, string | undefined>)[key];
    if (ko == null && import.meta.env?.DEV && !warned.has(`ko:${key}`)) { warned.add(`ko:${key}`); console.warn(`[i18n] missing ko: ${key}`); }
    text = ko ?? entry.en ?? entry.zh;
  } else {
    text = lang === 'en' ? entry.en : entry.zh;
  }
  return interpolate(text, vars);
}

/** 按当前语言取文案（组件外/工具函数可用） */
export function tr(key: string, vars?: TrVars): string {
  return translate(currentLang, key, vars);
}

/** 传给后端的语言（邮件模板、分享卡片等只支持中/英）：中文界面传 zh，其余传 en */
export function apiLang(lang: Lang = currentLang): 'zh' | 'en' {
  return lang === 'zh' ? 'zh' : 'en';
}
